'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Coins, Star, Mountain } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function DeniersPage() {
  const deniersCards = [
    { number: 'As', name: 'As de Deniers', meaning: 'Yeni fırsat, maddi başlangıç' },
    { number: '2', name: 'Deux de Deniers', meaning: 'Denge, çoklu görev' },
    { number: '3', name: 'Trois de Deniers', meaning: 'İş birliği, ustalık' },
    { number: '4', name: 'Quatre de Deniers', meaning: 'Güvenlik, kontrolsüzlük' },
    { number: '5', name: 'Cinq de Deniers', meaning: 'Yoksulluk, dışlanma' },
    { number: '6', name: 'Six de Deniers', meaning: 'Cömertlik, paylaşım' },
    { number: '7', name: 'Sept de Deniers', meaning: 'Sabır, yatırım' },
    { number: '8', name: 'Huit de Deniers', meaning: 'Ustalık, çalışkanlık' },
    { number: '9', name: 'Neuf de Deniers', meaning: 'Bolluk, lüks' },
    { number: '10', name: 'Dix de Deniers', meaning: 'Aile zenginliği, miras' },
    { number: 'V', name: 'Valet de Deniers', meaning: 'Öğrenci, fırsat' },
    { number: 'C', name: 'Cavalier de Deniers', meaning: 'Çalışkanlık, düzen' },
    { number: 'D', name: 'Dame de Deniers', meaning: 'Pratik bilgelik, doğa' },
    { number: 'R', name: 'Roi de Deniers', meaning: 'Maddi başarı, cömertlik' }
  ]

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Deniers</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Minör Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl p-8 mb-8 border border-green-600/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center">
              <Coins className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-100">Deniers (Altınlar)</h1>
              <p className="text-xl text-green-400">Toprak Elementi - Maddi Dünya, Pratiklik, Güvenlik</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            Marseille geleneğinde toprak elementini temsil eden Deniers takımı, maddi dünyamızı, pratik konuları ve güvenlik ihtiyaçlarımızı simgeler.
            Bu kartlar para, kariyer, sağlık ve fiziksel varlık konularında rehberlik sunar.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deniers Kartları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {deniersCards.map((card, index) => {
              const cardSlug = card.number === 'As' ? 'as' :
                             card.number === 'V' ? 'valet' :
                             card.number === 'C' ? 'cavalier' :
                             card.number === 'D' ? 'dame' :
                             card.number === 'R' ? 'roi' :
                             card.number === '2' ? 'deux' :
                             card.number === '3' ? 'trois' :
                             card.number === '4' ? 'quatre' :
                             card.number === '5' ? 'cinq' :
                             card.number === '6' ? 'six' :
                             card.number === '7' ? 'sept' :
                             card.number === '8' ? 'huit' :
                             card.number === '9' ? 'neuf' :
                             card.number === '10' ? 'dix' : card.number.toLowerCase();

              // Card visual representations
              const getCardVisual = (number) => {
                if (number === 'As') return '💰';
                if (number === 'V') return '🧑';
                if (number === 'C') return '🏇';
                if (number === 'D') return '👸';
                if (number === 'R') return '👑';
                return '🪙';
              };

              return (
                <Link
                  key={card.number}
                  href={`/about/tarot/systems/marseille/minor-arcana/deniers/${cardSlug}`}
                  className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-green-500/50 transition-colors group cursor-pointer"
                >
                  <div className="text-center">
                    {/* Card Visual */}
                    <div className="w-20 h-28 mx-auto mb-4 bg-gradient-to-b from-green-100 to-emerald-50 rounded-lg border border-green-300 flex flex-col items-center justify-center relative group-hover:scale-105 transition-transform shadow-lg">
                      <div className="text-2xl mb-1">{getCardVisual(card.number)}</div>
                      <div className="text-xs font-bold text-green-800">{card.number}</div>
                      <div className="absolute top-1 left-1 text-green-600">
                        <Coins className="h-2 w-2" />
                      </div>
                      <div className="absolute top-1 right-1 text-green-600">
                        <Coins className="h-2 w-2" />
                      </div>
                      <div className="absolute bottom-1 left-1 text-green-600">
                        <Mountain className="h-2 w-2" />
                      </div>
                      <div className="absolute bottom-1 right-1 text-green-600">
                        <Mountain className="h-2 w-2" />
                      </div>
                    </div>

                    <h3 className="font-semibold text-slate-100 mb-2 text-sm group-hover:text-green-400 transition-colors">{card.name}</h3>
                    <p className="text-xs text-slate-400">{card.meaning}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Suit Information */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6 flex items-center gap-2">
            <Mountain className="h-6 w-6 text-green-400" />
            Deniers Takımının Özellikleri
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-100 mb-3">Element ve Enerji</h3>
              <p className="text-slate-300 mb-4">Toprak - Maddi dünya, pratiklik, güvenlik ve istikrar</p>

              <h3 className="font-semibold text-slate-100 mb-3">Temsil Ettiği Alanlar</h3>
              <ul className="text-slate-300 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Para ve finansal durumlar
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Kariyer ve iş başarısı
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Sağlık ve fiziksel varlık
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  Ev ve güvenlik
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-slate-100 mb-3">Marseille Tradisyonu</h3>
              <p className="text-slate-300 mb-4">
                Deniers, Marseille tarotunda toprak elementini ve maddi dünyayı temsil eder.
                Geleneksel çizimler, altın sikke şeklindedir.
              </p>

              <h3 className="font-semibold text-slate-100 mb-3">Court Kartları</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Valet:</strong> Çalışkan öğrenci</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Cavalier:</strong> Güvenilir işçi</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Dame:</strong> Pratik ana figür</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Roi:</strong> Maddi başarı</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/coupes"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Coupes
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Minör Arkana'ya Dön
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  )
}